import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { Link } from 'react-router-dom';
import CommonHeader from '../../../util_components/common_header';
import CustomSingleSelect from '../../../util_components/custom_single_select';
import CustomDatePicker from '../../../util_components/custom_date_picker';
import { get_api } from '../../../../redux/api_funcs';
import { get_api_url } from '../../../../utils/urls';
import { day_format, time_format } from '../../../../constants';
import { fmt_amount, status_badge } from './helpers';

// Quick-view tabs → the goserver `view` query param ('all' sends no preset).
const VIEWS = [
  { key: 'needs_action', label: 'Needs action' },
  { key: 'unaddressed', label: 'Unaddressed' },
  { key: 'lost', label: 'Lost' },
  { key: 'won', label: 'Won' },
  { key: 'all', label: 'All' }
];

const KIND_OPTIONS = [
  { value: '', label: 'All kinds' },
  { value: 'dispute', label: 'Chargebacks' },
  { value: 'early_fraud_warning', label: 'Near (EFW)' }
];

const csv_cell = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

class Chargebacks extends Component {
  state = {
    loading: true,
    rows: [],
    total: 0,
    view: 'needs_action',
    kind: '',
    start_date: moment().subtract(90, 'days').format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
    limit: 100,
    offset: 0
  };

  componentDidMount() {
    this.load_data();
  }

  load_data = () => {
    this.setState({ loading: true }, () => {
      const { view, kind, start_date, end_date, limit, offset } = this.state;
      const qp = new URLSearchParams();
      if (view && view !== 'all') qp.set('view', view);
      if (kind) qp.set('kind', kind);
      if (start_date) qp.set('start_date', start_date);
      if (end_date) qp.set('end_date', end_date);
      qp.set('limit', limit);
      qp.set('offset', offset);

      const url = get_api_url(`/v2/cms_user/chargebacks?${qp.toString()}`, false, true);
      // get_api returns the raw axios response — the goserver payload is at res.data.data.
      get_api(url, true)
        .then((res) => {
          const payload = (res && res.data && res.data.data) || {};
          this.setState({
            loading: false,
            rows: payload.rows || [],
            total: payload.total || 0
          });
        })
        .catch(() => this.setState({ loading: false, rows: [], total: 0 }));
    });
  };

  set_view = (view) => this.setState({ view, offset: 0 }, this.load_data);

  handle_date_change = (name, val) => this.setState({ [name]: val });

  change_kind = (e) => this.setState({ kind: e.target.value, offset: 0 }, this.load_data);

  download_csv = () => {
    const { rows } = this.state;
    const headers = [
      'kind',
      'stripe_dispute_id',
      'charge_id',
      'student_name',
      'student_email',
      'amount_minor',
      'currency',
      'reason',
      'status',
      'internal_status',
      'match_status',
      'handled_by',
      'stripe_created_date'
    ];
    const lines = rows.map((r) => headers.map((h) => csv_cell(r[h])).join(','));
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `chargebacks-${moment().format('YYYY-MM-DD')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  render_filters = () => {
    const { view, kind, start_date, end_date } = this.state;
    return (
      <div className="flex flex-col gap-3 mb-3">
        <div className="flex flex-wrap gap-2 justify-center">
          {VIEWS.map((v) => (
            <button
              key={v.key}
              type="button"
              className="ct-small-btn"
              onClick={() => this.set_view(v.key)}
              style={{
                background: view === v.key ? 'var(--brand-primary)' : 'var(--surface-card)',
                color: view === v.key ? 'var(--text-inverse)' : 'var(--text-primary)',
                border: '1px solid var(--border-default)'
              }}
            >
              {v.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3 justify-center">
          <div className="daily-report-item">
            <CustomDatePicker
              label="Start Date"
              value={start_date}
              handleChange={(val) => this.handle_date_change('start_date', val)}
              maxDate={end_date}
            />
          </div>
          <div className="daily-report-item">
            <CustomDatePicker
              label="End Date"
              value={end_date}
              handleChange={(val) => this.handle_date_change('end_date', val)}
              minDate={start_date}
              maxDate={moment().format('YYYY-MM-DD')}
            />
          </div>
          <div style={{ width: '180px' }}>
            <CustomSingleSelect
              label="Kind"
              options={KIND_OPTIONS}
              onChange={this.change_kind}
              value={kind}
            />
          </div>
          <button type="button" className="ct-small-btn" onClick={this.load_data}>
            Apply
          </button>
          <button type="button" className="ct-small-btn" onClick={this.download_csv}>
            Download CSV
          </button>
        </div>
      </div>
    );
  };

  render_row = (r) => {
    const { iana_timezone } = this.props;
    const badge = status_badge(r.status);
    const detail_id = r.stripe_dispute_id; // EFW rows have no dispute-detail page
    return (
      <div className="ct-row" key={`${r.kind}-${r.id}`}>
        <div className="ct-col ct-qf ct-vsm-font ct-bw">
          <div>{r.kind === 'early_fraud_warning' ? 'EFW' : 'Dispute'}</div>
          <div className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
            {r.stripe_dispute_id || r.early_fraud_warning_id || '--'}
          </div>
        </div>
        <div className="ct-col ct-hf ct-la ct-bw">
          {r.student_uuid ? (
            <a
              className="link-no-dec"
              href={`/studentprofile?id=${r.student_uuid}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div>{r.student_name || '--'}</div>
              <div className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
                {r.student_email || ''}
              </div>
            </a>
          ) : (
            <span style={{ color: 'var(--text-muted)' }}>no student</span>
          )}
        </div>
        <div className="ct-col ct-qf ct-sm-font">{fmt_amount(r.amount_minor, r.currency)}</div>
        <div className="ct-col ct-qf ct-vsm-font">{r.reason || '--'}</div>
        <div className="ct-col ct-qf ct-vsm-font">
          <span
            style={{
              ...badge,
              padding: '2px 8px',
              borderRadius: '10px',
              display: 'inline-block',
              fontWeight: 600
            }}
          >
            {r.status || '--'}
          </span>
          {r.match_status && r.match_status !== 'matched' ? (
            <div className="ct-vsm-font" style={{ color: 'var(--state-warning-text)' }}>
              ⚠ {r.match_status}
            </div>
          ) : null}
        </div>
        <div className="ct-col ct-qf ct-vsm-font">
          {r.evidence_due_by
            ? moment(r.evidence_due_by).tz(iana_timezone).format(day_format)
            : '--'}
        </div>
        <div className="ct-col ct-qf ct-vsm-font ct-bw">
          <div>{r.internal_status || '--'}</div>
          {r.handled_by ? (
            <div className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
              {r.handled_by}
            </div>
          ) : null}
        </div>
        <div className="ct-col ct-qf ct-sm-font">
          <div>
            {r.stripe_created_date
              ? moment(r.stripe_created_date).tz(iana_timezone).format(day_format)
              : '--'}
          </div>
          <div className="ct-vsm-font" style={{ color: 'var(--text-muted)' }}>
            {r.stripe_created_date
              ? moment(r.stripe_created_date).tz(iana_timezone).format(time_format)
              : ''}
          </div>
        </div>
        <div className="ct-col ct-qf ct-vsm-font">
          {detail_id ? (
            <Link className="btn-link" to={`/chargebackdetails?id=${detail_id}`}>
              Details
            </Link>
          ) : (
            <span style={{ color: 'var(--text-muted)' }}>--</span>
          )}
        </div>
      </div>
    );
  };

  render() {
    const { loading, rows, total } = this.state;
    return (
      <CommonHeader loading={loading} title="Chargebacks">
        {this.render_filters()}
        <div className="ct-vsm-font mb-1" style={{ color: 'var(--text-muted)' }}>
          {total} result{total === 1 ? '' : 's'}
          {total > rows.length
            ? ` (showing first ${rows.length} — narrow the filters to see more)`
            : ''}
        </div>
        <div className="ct-table" style={{ minWidth: '1100px' }}>
          <div className="ct-row ct-header-row">
            <div className="ct-col ct-qf">Dispute</div>
            <div className="ct-col ct-hf ct-la">Student</div>
            <div className="ct-col ct-qf">Amount</div>
            <div className="ct-col ct-qf">Reason</div>
            <div className="ct-col ct-qf">Status</div>
            <div className="ct-col ct-qf">Evidence due</div>
            <div className="ct-col ct-qf">Internal</div>
            <div className="ct-col ct-qf">Created</div>
            <div className="ct-col ct-qf" />
          </div>
          {rows.length === 0 && !loading ? (
            <div className="ct-row">
              <div className="ct-col" style={{ color: 'var(--text-muted)' }}>
                No chargebacks for this filter.
              </div>
            </div>
          ) : (
            rows.map(this.render_row)
          )}
        </div>
      </CommonHeader>
    );
  }
}

const mapStateToProps = (state) => ({
  iana_timezone: state.home.iana_timezone
});

export default connect(mapStateToProps)(Chargebacks);
